import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { http, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import Navbar from "@/components/Navbar";
import Seo from "@/components/Seo";
import { Sparkles, ShieldCheck } from "lucide-react";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [twofaCode, setTwofaCode] = useState("");
  const [twofaRequired, setTwofaRequired] = useState(false);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { refresh } = useAuth();

  const goToDashboard = (role) => {
    navigate(
      role === "institute_admin" ? "/institute" :
      role === "self_study_student" ? "/selfstudy" :
      role === "super_admin" ? "/admin" :
      "/student"
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      const body = { email, password };
      if (twofaRequired) body.twofa_code = twofaCode;
      const { data } = await http.post("/auth/login", body);
      if (data.twofa_required) {
        setTwofaRequired(true);
        setLoading(false);
        return;
      }
      await refresh();
      // If the user hasn't verified their email yet (and isn't a super_admin),
      // route to the verify page so the option is always reachable on next login.
      if (data.user && data.user.email_verified === false && data.user.role !== "super_admin") {
        navigate("/verify-email");
        return;
      }
      goToDashboard(data.user.role);
    } catch (e2) {
      setErr(formatApiError(e2));
      setLoading(false);
    }
  };

  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <Seo
        title="Sign in to VidyaAI Study | AI Tutor for Institutes & Students"
        description="Sign in to VidyaAI Study — your personal AI tutor on web & Telegram. One login for institute admins, students and self-study learners."
        path="/login"
        noindex={true}
      />
      <Navbar variant="auth" />
      <div className="container-x py-20">
        <div className="max-w-md mx-auto">
          <div className="label-eyebrow mb-4">{twofaRequired ? "Two-factor check" : "Welcome back"}</div>
          <h1 className="font-display text-3xl sm:text-4xl tracking-tighter mb-3">
            {twofaRequired ? "Enter your 6-digit code" : "Sign in to VidyaAI Study"}
          </h1>
          {!twofaRequired && (
            <p className="text-sm text-[#7A7369] mb-10">
              One login for everyone — institute admins, students from a coaching, and self-study learners. We detect your role automatically.
            </p>
          )}
          {twofaRequired && <div className="mb-10" />}

          <form onSubmit={handleSubmit} className="space-y-5" data-testid="login-form">
            {!twofaRequired ? (
              <>
                <div>
                  <label className="block text-sm text-[#4A4640] mb-2">Email or mobile</label>
                  <input
                    type="text" required value={email} onChange={(e) => setEmail(e.target.value)}
                    className="input-field" placeholder="you@institute.in or +91 98765 43210" data-testid="login-email"
                    autoComplete="username"
                  />
                </div>
                <div>
                  <label className="block text-sm text-[#4A4640] mb-2">Password</label>
                  <input
                    type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                    className="input-field" placeholder="••••••••" data-testid="login-password"
                  />
                </div>
              </>
            ) : (
              <div>
                <div className="flex items-center gap-2 text-sm text-[#4A4640] mb-3">
                  <ShieldCheck size={16} strokeWidth={1.6} className="text-[#C24933]" />
                  <span>Open your authenticator app and enter the code for <strong className="text-[#1A1713]">{email}</strong>.</span>
                </div>
                <input
                  type="text" inputMode="numeric" maxLength={6} pattern="\d{6}" required autoFocus
                  value={twofaCode} onChange={(e) => setTwofaCode(e.target.value.replace(/\D/g, ""))}
                  className="input-field font-mono tracking-[0.5em] text-center text-2xl"
                  placeholder="000000"
                  data-testid="login-twofa-code"
                />
              </div>
            )}

            {err && <div className="text-sm text-[#B73B3B]" data-testid="login-error">{err}</div>}

            <button type="submit" disabled={loading || (twofaRequired && twofaCode.length !== 6)} className="btn-primary w-full" data-testid="login-submit">
              {loading ? "Signing in…" : twofaRequired ? "Verify code" : "Sign in"}
            </button>

            {twofaRequired && (
              <button type="button" onClick={() => { setTwofaRequired(false); setTwofaCode(""); setErr(""); }} className="btn-ghost w-full text-sm" data-testid="login-twofa-cancel">
                ← Back to password
              </button>
            )}
          </form>

          {!twofaRequired && (
            <>
              <div className="flex items-center gap-4 my-8">
                <div className="flex-1 h-px bg-[#E6E2D8]" />
                <span className="text-xs text-[#7A7369] uppercase tracking-wider">or</span>
                <div className="flex-1 h-px bg-[#E6E2D8]" />
              </div>

              <button onClick={handleGoogle} className="btn-ghost w-full" data-testid="login-google">
                <Sparkles size={16} strokeWidth={1.6} /> Continue with Google
              </button>

              <p className="mt-8 text-sm text-[#4A4640]">
                New here?{" "}
                <Link to="/register" className="text-[#C24933] hover:underline" data-testid="login-to-register">Create an account</Link>
              </p>
              <p className="mt-2 text-sm text-[#4A4640]">
                Forgot your password?{" "}
                <Link to="/forgot-password" className="text-[#C24933] hover:underline" data-testid="login-forgot">Reset it</Link>
              </p>
              <p className="mt-2 text-xs text-[#7A7369]">Need help signing in? Email <a href="mailto:info@vidyaai.study" className="text-[#C24933] hover:underline">info@vidyaai.study</a></p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
